import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatINR } from "../utils/formatINR";
import { DateTime, MessageRow, Money, Mono, StatusBadge, TableCard, Td, Th, rowClass, tableClass, tbodyClass, theadClass } from "./ui/DataPage";

// --- Helpers ---
// formatters are created once; building them per row is slow on large tables
const dateFormatter = new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const timeFormatter = new Intl.DateTimeFormat("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });

const PAGE_SIZE = 100;

// the session last opened from this table, so it can be marked on the way back
// (sessionStorage: survives navigation and reloads, cleared when the tab closes)
const LAST_VIEWED_KEY = "charging_sessions:last_viewed";

const readLastViewed = () => {
  try {
    return sessionStorage.getItem(LAST_VIEWED_KEY);
  } catch {
    return null;
  }
};

const saveLastViewed = (id) => {
  try {
    sessionStorage.setItem(LAST_VIEWED_KEY, String(id));
  } catch {
    // storage blocked: the row just won't be marked
  }
};

// enough pages to include the row at `index`
const countToShow = (index) => (index < 0 ? PAGE_SIZE : Math.ceil((index + 1) / PAGE_SIZE) * PAGE_SIZE);

const formatCurrency = (value) => formatINR(value || 0);

const formatDateTime = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    return dateFormatter.format(new Date(timestamp));
  } catch {
    return "Invalid Date";
  }
};

const formatTime = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    return timeFormatter.format(new Date(timestamp));
  } catch {
    return "Invalid Time";
  }
};

// settlement_transactions.status values
const SETTLEMENT_TONES = {
  settled: "green",
  pending: "amber",
  processing: "brand",
  failed: "red",
  skipped: "neutral",
};

const SettlementStatus = ({ session, available }) => {
  if (!available) return <span className="text-neutral-300">—</span>;
  const status = session.settlement_status;
  if (!status) {
    return (
      <span className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium text-neutral-500 ring-1 ring-inset ring-neutral-200">
        No record
      </span>
    );
  }
  return (
    <StatusBadge
      tone={SETTLEMENT_TONES[status] || "neutral"}
      title={status === "skipped" && session.settlement_skip_reason ? `Skipped: ${session.settlement_skip_reason}` : undefined}
    >
      <span className="capitalize">{status}</span>
    </StatusBadge>
  );
};

// --- Main SessionsTable Component ---
const SessionsTable = ({ sessions, totalCount, isLoading = false, settlementAvailable = false }) => {
  const navigate = useNavigate();
  const [lastViewedId] = useState(readLastViewed);
  const lastViewedIndex = lastViewedId ? sessions.findIndex((s) => String(s.id) === lastViewedId) : -1;
  const [visibleCount, setVisibleCount] = useState(() => countToShow(lastViewedIndex));
  const lastViewedRef = useRef(null);
  const scrolledRef = useRef(false);

  // start from the first page again whenever the data or filters change,
  // but keep the last viewed session on screen
  useEffect(() => {
    setVisibleCount(countToShow(lastViewedIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  // bring the last viewed session into view once, when coming back to the page
  useEffect(() => {
    if (scrolledRef.current || !lastViewedRef.current) return;
    scrolledRef.current = true;
    lastViewedRef.current.scrollIntoView({ block: "center" });
  });

  const openSession = (id) => {
    saveLastViewed(id);
    navigate(`/charging-sessions/${id}`);
  };

  const visibleSessions = sessions.slice(0, visibleCount);

  return (
    <TableCard
      header={
        <span>
          Showing <span className="font-semibold tabular-nums text-brand-950">{sessions.length}</span> of{" "}
          <span className="font-semibold tabular-nums text-brand-950">{totalCount}</span> sessions
        </span>
      }
      footer={
        sessions.length > visibleCount && (
          <button onClick={() => setVisibleCount((c) => c + PAGE_SIZE)} className="btn-secondary">
            Show {Math.min(PAGE_SIZE, sessions.length - visibleCount)} more
          </button>
        )
      }
    >
      <table className={tableClass}>
        <thead className={theadClass}>
          <tr>
            <Th>Session Start</Th>
            <Th>Session End</Th>
            <Th>Vehicle</Th>
            <Th>Station/Gun</Th>
            <Th align="right">Final Cost</Th>
            <Th title={settlementAvailable ? undefined : "Settlement data is not available from the server yet"}>Settlement Status</Th>
            <Th align="right">Settlement Amount</Th>
          </tr>
        </thead>
        <tbody className={tbodyClass}>
          {visibleSessions.map((session) => {
            const isLastViewed = lastViewedId !== null && String(session.id) === lastViewedId;
            return (
            <tr
              key={session.id}
              ref={isLastViewed ? lastViewedRef : undefined}
              className={`${rowClass} cursor-pointer ${isLastViewed ? "!bg-brand-50" : ""}`}
              onClick={() => openSession(session.id)}
            >
              <Td className={isLastViewed ? "shadow-[inset_3px_0_0_#4f46e5]" : ""}>
                <div className="flex items-center gap-2.5">
                  <DateTime date={formatDateTime(session.session_start)} time={formatTime(session.session_start)} />
                  {isLastViewed && (
                    <span className="rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                      Last viewed
                    </span>
                  )}
                </div>
              </Td>
              <Td>
                {session.session_end ? (
                  <DateTime date={formatDateTime(session.session_end)} time={formatTime(session.session_end)} />
                ) : (
                  <StatusBadge tone="amber" pulse>
                    Active
                  </StatusBadge>
                )}
              </Td>
              <Td>
                <Mono max="max-w-[200px]">{session.vehicle_id || ""}</Mono>
              </Td>
              <Td>
                <span className="font-medium text-neutral-800">{session.station_id}</span>
                <span className="text-neutral-400"> / </span>
                <span className="text-neutral-600">{session.gun_id}</span>
              </Td>
              <Td align="right">
                <Money>{formatCurrency(session.final_cost)}</Money>
              </Td>
              <Td>
                <SettlementStatus session={session} available={settlementAvailable} />
              </Td>
              <Td align="right">
                {settlementAvailable && session.settlement_status ? (
                  <Money>{formatINR(session.settlement_amount)}</Money>
                ) : (
                  <span className="text-neutral-300">—</span>
                )}
              </Td>
            </tr>
            );
          })}
          {sessions.length === 0 && <MessageRow colSpan={7}>{isLoading ? "Loading sessions..." : "No sessions found."}</MessageRow>}
        </tbody>
      </table>
    </TableCard>
  );
};

export default SessionsTable;
